import React from "react";
import type { StudioWithAccess } from "../api/types";
import { useAuth } from "../auth/useAuth";
import { useUiPreferences } from "../prefs/useUiPreferences";

export type UseCheckInDeviceLocationResult = {
  locationStudioId: string | null;
  setLocationStudioId: (id: string | null) => void;
};

function readStoredLocationId(storageKey: string): string | null {
  try {
    const value = (localStorage.getItem(storageKey) || "").trim();
    return value || null;
  } catch {
    return null;
  }
}

/**
 * Check-in-Standort pro Browser/Gerät (nicht der globale Header-Kontext).
 * Speicher: `checkinLocationStudioId.{accountId}`; einmalige Migration von `checkinStudioId.{accountId}` und optional einmalig `checkInPage.studioId` aus Server-Prefs.
 */
export function useCheckInDeviceLocation(
  allowedStudios: StudioWithAccess[],
  studiosLoaded: boolean
): UseCheckInDeviceLocationResult {
  const auth = useAuth();
  const ui = useUiPreferences();

  const accountId = auth.info?.accountId ? String(auth.info.accountId) : "anon";
  const storageKey = `checkinLocationStudioId.${accountId}`;
  const legacyStorageKey = `checkinStudioId.${accountId}`;

  const [locationStudioId, setLocationStudioIdState] = React.useState<string | null>(() => readStoredLocationId(storageKey));

  const initDoneRef = React.useRef(false);

  const persistId = React.useCallback(
    (id: string | null) => {
      try {
        if (id) localStorage.setItem(storageKey, id);
        else localStorage.removeItem(storageKey);
      } catch {
        // ignore
      }
    },
    [storageKey]
  );

  const readStoredId = React.useCallback((): string | null => {
    return readStoredLocationId(storageKey);
  }, [storageKey]);

  React.useEffect(() => {
    initDoneRef.current = false;
    setLocationStudioIdState(readStoredLocationId(storageKey));
  }, [accountId, storageKey]);

  React.useEffect(() => {
    if (!ui.isReady || !studiosLoaded) return;
    if (initDoneRef.current) return;

    if (allowedStudios.length === 0) {
      setLocationStudioIdState(null);
      initDoneRef.current = true;
      return;
    }

    if (allowedStudios.length === 1) {
      const onlyId = allowedStudios[0]!.id;
      setLocationStudioIdState(onlyId);
      persistId(onlyId);
      initDoneRef.current = true;
      return;
    }

    const allowed = new Set(allowedStudios.map((s) => s.id));
    let resolved = readStoredId();
    if (resolved && !allowed.has(resolved)) {
      resolved = null;
      persistId(null);
    }

    if (!resolved) {
      try {
        const leg = (localStorage.getItem(legacyStorageKey) || "").trim();
        if (leg && allowed.has(leg)) {
          resolved = leg;
          persistId(leg);
          try {
            localStorage.removeItem(legacyStorageKey);
          } catch {
            // ignore
          }
        }
      } catch {
        // ignore
      }
    }

    if (!resolved) {
      const srv = String(ui.prefs.checkInPage?.studioId || "").trim();
      if (srv && allowed.has(srv)) {
        resolved = srv;
        persistId(srv);
      }
    }

    if (!resolved) {
      setLocationStudioIdState(null);
    } else {
      setLocationStudioIdState(resolved);
    }

    initDoneRef.current = true;
  }, [
    ui.isReady,
    studiosLoaded,
    allowedStudios,
    legacyStorageKey,
    persistId,
    readStoredId,
    ui.prefs.checkInPage?.studioId
  ]);

  React.useEffect(() => {
    if (!initDoneRef.current || !studiosLoaded) return;

    if (allowedStudios.length === 0) {
      if (locationStudioId !== null) setLocationStudioIdState(null);
      return;
    }

    if (allowedStudios.length === 1) {
      const onlyId = allowedStudios[0]!.id;
      if (locationStudioId !== onlyId) {
        setLocationStudioIdState(onlyId);
        persistId(onlyId);
      }
      return;
    }

    if (locationStudioId && !allowedStudios.some((s) => s.id === locationStudioId)) {
      setLocationStudioIdState(null);
      persistId(null);
    }
  }, [allowedStudios, studiosLoaded, locationStudioId, persistId]);

  const setLocationStudioId = React.useCallback(
    (id: string | null) => {
      setLocationStudioIdState(id);
      persistId(id);
    },
    [persistId]
  );

  return {
    locationStudioId,
    setLocationStudioId
  };
}
